import { Bell, FileText, Users } from 'lucide-react'

const FEATURES = [
  {
    icon: Users,
    title: 'Lead Management',
    description: 'Capture, track and manage leads from all sources.',
    iconClass: 'text-cyan-400',
    boxClass: 'border-cyan-500/25 bg-cyan-500/[0.06]',
  },
  {
    icon: Bell,
    title: 'Smart Follow-ups',
    description: 'Never miss a follow-up. Convert more leads.',
    iconClass: 'text-violet-400',
    boxClass: 'border-violet-500/25 bg-violet-500/[0.06]',
  },
  {
    icon: FileText,
    title: 'Quotation Management',
    description: 'Create, send and manage quotations seamlessly.',
    iconClass: 'text-blue-400',
    boxClass: 'border-blue-500/25 bg-blue-500/[0.06]',
  },
]

export function LoginBrandPanel() {
  return (
    <section className="relative hidden h-dvh min-h-0 overflow-hidden bg-[#020817] text-white md:block">
      {/* ================= BACKGROUND ================= */}

      {/* Deep navy base */}
      <div className="absolute inset-0 bg-[#020817]" />

      {/* Subtle blue/purple atmospheric glow */}
      <div
        className="
          absolute inset-0
          bg-[radial-gradient(circle_at_35%_45%,rgba(14,165,233,0.08),transparent_32%),radial-gradient(circle_at_72%_70%,rgba(124,58,237,0.18),transparent_42%)]
        "
      />

      {/* Fine grid */}
      <div
        className="
          absolute inset-0
          opacity-[0.08]
          [background-image:
            linear-gradient(rgba(100,116,139,0.55)_1px,transparent_1px),
            linear-gradient(90deg,rgba(100,116,139,0.55)_1px,transparent_1px)
          ]
          [background-size:38px_38px]
        "
      />

      {/* Large soft purple glow */}
      <div
        className="
          absolute
          right-[-18%]
          bottom-[-18%]
          h-[560px]
          w-[650px]
          rounded-full
          bg-violet-700/[0.20]
          blur-[120px]
        "
      />

      {/* Cyan glow near bottom-left */}
      <div
        className="
          absolute
          left-[-18%]
          bottom-[-12%]
          h-[320px]
          w-[600px]
          rounded-full
          bg-cyan-500/[0.12]
          blur-[100px]
        "
      />

      {/* Purple dotted mesh */}
      <div
        className="
          absolute
          right-[-4%]
          bottom-[2%]
          h-[260px]
          w-[62%]
          opacity-[0.65]
          [background-image:radial-gradient(circle,rgba(168,85,247,0.7)_1.15px,transparent_1.15px)]
          [background-size:8px_8px]
          [mask-image:radial-gradient(ellipse_at_center,black_5%,black_42%,transparent_78%)]
        "
      />

      {/* Cyan dotted mesh */}
      <div
        className="
          absolute
          left-[-10%]
          bottom-[-2%]
          h-[170px]
          w-[48%]
          opacity-[0.55]
          [background-image:radial-gradient(circle,rgba(34,211,238,0.65)_1px,transparent_1px)]
          [background-size:7px_7px]
          [mask-image:radial-gradient(ellipse_at_center,black_0%,transparent_72%)]
        "
      />

      {/* Purple curved wave */}
      <div
        className="
          absolute
          right-[-20%]
          bottom-[10%]
          h-[260px]
          w-[80%]
          rotate-[-8deg]
          rounded-[50%]
          border-t
          border-violet-500/25
          shadow-[0_0_30px_rgba(139,92,246,0.08)]
        "
      />

      {/* Second purple wave */}
      <div
        className="
          absolute
          right-[-18%]
          bottom-[5%]
          h-[230px]
          w-[76%]
          rotate-[-8deg]
          rounded-[50%]
          border-t
          border-violet-400/10
        "
      />

      {/* Cyan curved wave */}
      <div
        className="
          absolute
          left-[-20%]
          bottom-[-8%]
          h-[190px]
          w-[75%]
          rotate-[7deg]
          rounded-[50%]
          border-t
          border-cyan-400/25
          shadow-[0_0_25px_rgba(34,211,238,0.08)]
        "
      />

      {/* Second cyan wave */}
      <div
        className="
          absolute
          left-[-15%]
          bottom-[-13%]
          h-[170px]
          w-[70%]
          rotate-[7deg]
          rounded-[50%]
          border-t
          border-cyan-400/10
        "
      />

      {/* Large subtle curved arc */}
      <div
        className="
          absolute
          right-[-260px]
          top-[18%]
          h-[600px]
          w-[600px]
          rounded-full
          border
          border-indigo-400/[0.08]
        "
      />

      {/* Small curved arc */}
      <div
        className="
          absolute
          right-[-180px]
          top-[30%]
          h-[430px]
          w-[430px]
          rounded-full
          border
          border-violet-400/[0.07]
        "
      />

      {/* Cyan ambient light */}
      <div
        className="
          absolute
          left-[62%]
          top-[35%]
          size-1.5
          rounded-full
          bg-cyan-300/60
          shadow-[0_0_18px_rgba(34,211,238,0.8)]
        "
      />

      {/* Purple ambient light */}
      <div
        className="
          absolute
          right-[9%]
          top-[16%]
          size-1.5
          rounded-full
          bg-violet-400/70
          shadow-[0_0_20px_rgba(139,92,246,0.9)]
        "
      />

      {/* Small purple light */}
      <div
        className="
          absolute
          right-[32%]
          bottom-[28%]
          size-1
          rounded-full
          bg-purple-400/60
          shadow-[0_0_14px_rgba(168,85,247,0.8)]
        "
      />

      {/* ================= CONTENT ================= */}

      <div className="relative z-10 flex h-full flex-col px-6 py-5 md:px-7 md:py-6 lg:px-8 lg:py-7 xl:px-14 xl:py-8">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white shadow-lg md:size-11">
            <img
              src="/logo.jpeg"
              alt="Superior CRM"
              className="h-full w-full object-contain p-1.5"
            />
          </div>

          <span className="text-[21px] font-bold tracking-tight md:text-[22px] xl:text-[24px]">
            SuperiorCRM
          </span>
        </div>

        {/* Badge */}
        <div className="mt-5 inline-flex w-fit items-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-400/[0.07] px-3.5 py-1.5 text-[11px] font-semibold text-slate-200 md:mt-6 md:px-4 md:py-2 md:text-[12px]">
          <Users className="size-3.5 text-cyan-400 md:size-4" />
          SALES &amp; LEAD MANAGEMENT
        </div>

        {/* Hero */}
        <div className="mt-10 max-w-[680px] lg:mt-12 xl:mt-[72px]">
          <h1 className="text-[clamp(2.2rem,4vw,4.5rem)] font-bold leading-[0.98] tracking-[-0.055em]">
            Manage leads.
            <br />
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-violet-400 bg-clip-text text-transparent">
              Close more deals.
            </span>
          </h1>

          <p className="mt-5 max-w-[610px] text-[13px] leading-6 text-slate-300 md:text-[14px] lg:mt-6 lg:text-[15px] xl:mt-7 xl:text-[17px]">
            One powerful workspace to manage your leads, follow-ups, sales team
            and quotations — all in one place.
          </p>
        </div>

        {/* Features */}
        <div className="mt-6 space-y-3 md:mt-7 md:space-y-3.5 lg:mt-8 xl:mt-9 xl:space-y-4">
          {FEATURES.map((feature) => {
            const Icon = feature.icon

            return (
              <div
                key={feature.title}
                className="flex items-center gap-3 md:gap-3.5 xl:gap-4"
              >
                <div
                  className={`grid size-10 shrink-0 place-items-center rounded-xl border md:size-11 xl:size-12 ${feature.boxClass}`}
                >
                  <Icon className={`size-4.5 md:size-5 ${feature.iconClass}`} />
                </div>

                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-white md:text-[14px] xl:text-[15px]">
                    {feature.title}
                  </p>

                  <p className="mt-0.5 text-[11px] leading-4 text-slate-400 md:text-[12px] md:leading-5 xl:text-[13px]">
                    {feature.description}
                  </p>
                </div>
              </div>
            )
          })}
        </div>

        {/* Footer */}
        <div className="mt-auto pt-4">
          <p className="text-[10px] text-slate-500 md:text-[11px] xl:text-[12px]">
            © 2026 Superior CRM
            <span className="mx-2">•</span>
            Built for India&apos;s sales teams
          </p>
        </div>
      </div>
    </section>
  )
}

export default LoginBrandPanel
